<template>
  <div
    class="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4 mb-3 sm:mb-5 w-full"
  >
    <!-- ======================================================= -->
    <!-- 1. STATUS RATIO GRAPH CARD (MOVED FIRST AS REQUESTED)   -->
    <!-- ======================================================= -->
    <!-- When NOT in passout class filter -->
    <div
      v-if="!isPassoutClass"
      class="col-span-2 lg:col-span-1 flex bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs flex-col justify-between hover:border-indigo-300 dark:hover:border-indigo-700/60 transition-all duration-200"
    >
      <div
        v-if="isLoading"
        class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-xs flex items-center justify-center rounded-xl z-10"
      >
        <AppLoader variant="inline" size="xs" color="indigo" />
      </div>

      <div>
        <div class="flex items-center justify-between mb-1.5">
          <div class="flex items-center gap-1.5 min-w-0">
            <div class="p-1.5 bg-indigo-50 dark:bg-indigo-950/40 rounded-lg text-indigo-600 dark:text-indigo-400 shrink-0">
              <PieChart class="w-4 h-4" />
            </div>
            <h3 class="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
              Status Ratio
            </h3>
          </div>

          <!-- SHOW IN GRAPH BUTTON (HERO ACTION) -->
          <button
            type="button"
            @click="isGraphModalOpen = true"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 border border-indigo-200/70 dark:border-indigo-800/50 transition-all duration-150 cursor-pointer active:scale-95 shadow-2xs group shrink-0"
            title="Open Interactive Graph Analytics Modal"
          >
            <BarChart3 class="w-3 h-3 group-hover:scale-110 transition-transform" />
            <span>Show in Graph</span>
            <ArrowUpRight class="w-2.5 h-2.5 opacity-70 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>
      </div>

      <!-- Graph with Total in Center, Active & Inactive breakdown -->
      <div class="flex items-center justify-between gap-3 my-1">
        <!-- Circular SVG Donut Chart with Total In Center (Clickable to open modal) -->
        <div
          class="relative flex items-center justify-center shrink-0 cursor-pointer group"
          @click="isGraphModalOpen = true"
          title="Click to view full graph breakdown"
        >
          <svg
            class="w-14 h-14 sm:w-16 sm:h-16 -rotate-90 transform group-hover:scale-105 transition-transform"
            viewBox="0 0 48 48"
            role="img"
            aria-label="Student status distribution"
          >
            <!-- Background Track -->
            <circle
              cx="24"
              cy="24"
              :r="r"
              fill="transparent"
              class="stroke-slate-100 dark:stroke-[#1E293B]"
              :stroke-width="stroke"
            />
            <!-- Active Segment (Emerald) -->
            <circle
              v-if="totalStatus > 0 && activeLen > 0"
              cx="24"
              cy="24"
              :r="r"
              fill="transparent"
              class="stroke-emerald-500 transition-all duration-500"
              :stroke-width="stroke"
              :stroke-dasharray="`${activeLen} ${circumference - activeLen}`"
              :stroke-dashoffset="0"
            />
            <!-- Inactive Segment (Rose) -->
            <circle
              v-if="totalStatus > 0 && inactiveLen > 0"
              cx="24"
              cy="24"
              :r="r"
              fill="transparent"
              class="stroke-rose-500 transition-all duration-500"
              :stroke-width="stroke"
              :stroke-dasharray="`${inactiveLen} ${circumference - inactiveLen}`"
              :stroke-dashoffset="`-${activeLen}`"
            />
          </svg>
          <!-- Center Total Count & Label -->
          <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center">
            <span class="text-xs sm:text-sm font-black text-slate-900 dark:text-white leading-none">
              <template v-if="!isLoading">{{ displayTotalStudents }}</template>
              <template v-else>--</template>
            </span>
            <span class="text-[8px] sm:text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-tighter mt-0.5">
              Total
            </span>
          </div>
        </div>

        <!-- Legend with Active & Inactive stats -->
        <div class="flex flex-col justify-center gap-1 flex-1 min-w-0">
          <!-- Active Line -->
          <div class="flex items-center justify-between text-xs">
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
              <span class="text-slate-600 dark:text-slate-400 text-[11px] truncate">Active</span>
            </div>
            <span class="font-bold text-slate-800 dark:text-slate-200 text-[11px]">
              {{ activeStudents }} <span class="text-slate-400 dark:text-slate-500 font-normal">({{ activePct }}%)</span>
            </span>
          </div>

          <!-- Inactive Line -->
          <div class="flex items-center justify-between text-xs">
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="w-2 h-2 rounded-full bg-rose-500 shrink-0"></span>
              <span class="text-slate-600 dark:text-slate-400 text-[11px] truncate">Inactive</span>
            </div>
            <span class="font-bold text-slate-800 dark:text-slate-200 text-[11px]">
              {{ inactiveStudents }} <span class="text-slate-400 dark:text-slate-500 font-normal">({{ inactivePct }}%)</span>
            </span>
          </div>

          <!-- Micro Progress Bar -->
          <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden flex mt-0.5">
            <div
              class="bg-emerald-500 h-full transition-all duration-500"
              :style="{ width: `${activePct}%` }"
            ></div>
            <div
              class="bg-rose-500 h-full transition-all duration-500"
              :style="{ width: `${inactivePct}%` }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Card Footer -->
      <div class="flex items-center justify-between mt-1 pt-1.5 border-t border-slate-100 dark:border-[#263449]/60 text-[11px]">
        <p class="text-slate-400 dark:text-slate-500 truncate">
          {{ activePct }}% active student body
        </p>
        <button
          type="button"
          @click="isGraphModalOpen = true"
          class="font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition flex items-center gap-0.5 cursor-pointer shrink-0"
        >
          <span>View Details</span>
          <ArrowUpRight class="w-3 h-3" />
        </button>
      </div>
    </div>

    <!-- Passout / Alumni Students Card (When in passout class filter, appears first) -->
    <div
      v-else
      class="col-span-2 sm:col-span-1 flex bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
    >
      <div
        v-if="isLoading"
        class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-xs flex items-center justify-center rounded-xl z-10"
      >
        <AppLoader variant="inline" size="xs" color="indigo" />
      </div>

      <div>
        <div class="flex items-center justify-between mb-1.5">
          <div class="p-1.5 bg-indigo-50 dark:bg-indigo-950/40 rounded-lg text-indigo-600 dark:text-indigo-400">
            <GraduationCap class="w-4 h-4" />
          </div>
          <div class="flex items-center gap-1.5">
            <span
              class="text-[10px] font-semibold bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded"
            >
              Alumni
            </span>
            <button
              type="button"
              @click="isGraphModalOpen = true"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 border border-indigo-200/70 dark:border-indigo-800/50 transition-all duration-150 cursor-pointer active:scale-95 shadow-2xs group shrink-0"
              title="Open Interactive Graph Analytics Modal"
            >
              <BarChart3 class="w-3 h-3 group-hover:scale-110 transition-transform" />
              <span>Show in Graph</span>
              <ArrowUpRight class="w-2.5 h-2.5 opacity-70 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>
        </div>
        <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300">
          Passout Students
        </h3>
        <div class="flex items-baseline justify-between gap-2 mt-0.5">
          <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            <template v-if="!isLoading">{{ graduatedStudents }}</template>
            <template v-else>--</template>
          </p>
          <span class="sm:hidden text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.5 rounded-md">
            {{ graduatedPct }}% Passed Out
          </span>
        </div>
      </div>
      <!-- Card Footer -->
      <div class="flex items-center justify-between mt-1 pt-1.5 border-t border-slate-100 dark:border-[#263449]/60 text-[11px]">
        <p class="text-slate-400 dark:text-slate-500 text-[11px]">
          {{ graduatedPct }}% graduation rate
        </p>
        <button
          type="button"
          @click="isGraphModalOpen = true"
          class="font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition flex items-center gap-0.5 cursor-pointer shrink-0"
        >
          <span>View Details</span>
          <ArrowUpRight class="w-3 h-3" />
        </button>
      </div>
    </div>

    <!-- ======================================================= -->
    <!-- 2. TOTAL STUDENTS CARD                                   -->
    <!-- ======================================================= -->
    <div
      v-if="!isPassoutClass"
      class="col-span-1 flex bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
    >
      <div
        v-if="isLoading"
        class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-xs flex items-center justify-center rounded-xl z-10"
      >
        <AppLoader variant="inline" size="xs" color="indigo" />
      </div>

      <div>
        <div class="flex items-center justify-between mb-1.5">
          <div class="p-1.5 bg-indigo-50 dark:bg-indigo-950/40 rounded-lg text-indigo-600 dark:text-indigo-400">
            <Users class="w-4 h-4" />
          </div>
          <span
            class="text-[10px] font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded"
          >
            Total
          </span>
        </div>
        <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300">
          Total Students
        </h3>
        <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
          <template v-if="!isLoading">{{ displayTotalStudents }}</template>
          <template v-else>--</template>
        </p>
      </div>
      <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1.5">
        Enrolled directory count
      </p>
    </div>

    <!-- ======================================================= -->
    <!-- 3. ACTIVE STUDENTS CARD                                  -->
    <!-- ======================================================= -->
    <div
      v-if="!isPassoutClass"
      class="col-span-1 flex bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
    >
      <div
        v-if="isLoading"
        class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-xs flex items-center justify-center rounded-xl z-10"
      >
        <AppLoader variant="inline" size="xs" color="emerald" />
      </div>

      <div>
        <div class="flex items-center justify-between mb-1.5">
          <div class="p-1.5 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg text-emerald-600 dark:text-emerald-400">
            <UserCheck class="w-4 h-4" />
          </div>
          <span
            class="text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded"
          >
            Active
          </span>
        </div>
        <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300">
          Active Students
        </h3>
        <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
          <template v-if="!isLoading">{{ activeStudents }}</template>
          <template v-else>--</template>
        </p>
      </div>
      <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1.5">
        {{ activePct }}% active rate
      </p>
    </div>

    <!-- ======================================================= -->
    <!-- 4. INACTIVE STUDENTS CARD                                -->
    <!-- ======================================================= -->
    <div
      v-if="!isPassoutClass"
      class="col-span-2 lg:col-span-1 flex bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
    >
      <div
        v-if="isLoading"
        class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-xs flex items-center justify-center rounded-xl z-10"
      >
        <AppLoader variant="inline" size="xs" color="amber" />
      </div>

      <div>
        <div class="flex items-center justify-between mb-1.5">
          <div class="p-1.5 bg-rose-50 dark:bg-rose-950/40 rounded-lg text-rose-600 dark:text-rose-400">
            <UserX class="w-4 h-4" />
          </div>
          <span
            class="text-[10px] font-semibold bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 px-1.5 py-0.5 rounded"
          >
            Inactive
          </span>
        </div>
        <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300">
          Inactive / Leaved
        </h3>
        <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
          <template v-if="!isLoading">{{ inactiveStudents }}</template>
          <template v-else>--</template>
        </p>
      </div>
      <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1.5">
        {{ inactivePct }}% of student body
      </p>
    </div>
  </div>

  <!-- MODERN GRAPH ANALYTICS MODAL -->
  <StudentStatusGraphModal
    :show="isGraphModalOpen"
    :students="students"
    :totalStudents="displayTotalStudents"
    :activeStudents="activeStudents"
    :graduatedStudents="graduatedStudents"
    :inactiveStudents="inactiveStudents"
    :classBreakdown="classBreakdown"
    @close="isGraphModalOpen = false"
  />
</template>

<script setup>
  import { ref, computed } from "vue";
  import {
    Users,
    UserCheck,
    GraduationCap,
    UserX,
    PieChart,
    BarChart3,
    ArrowUpRight,
  } from "lucide-vue-next";
  import StudentStatusGraphModal from "./StudentStatusGraphModal.vue";

  const props = defineProps({
    students: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    totalStudents: {
      type: Number,
      default: 0,
    },
    activeStudents: {
      type: Number,
      default: 0,
    },
    graduatedStudents: {
      type: Number,
      default: 0,
    },
    inactiveStudents: {
      type: Number,
      default: 0,
    },
    classBreakdown: {
      type: Array,
      default: () => [],
    },
    isPassoutClass: {
      type: Boolean,
      default: false,
    },
  });

  const isGraphModalOpen = ref(false);

  const displayTotalStudents = computed(() => {
    if (typeof props.totalStudents === "number" && props.totalStudents > 0) {
      return props.totalStudents;
    }
    return props.students.length || (props.activeStudents + props.inactiveStudents);
  });

  // Circle geometry for SVG Donut
  const r = 18;
  const stroke = 5.5;
  const circumference = computed(() => 2 * Math.PI * r);

  const totalStatus = computed(() => {
    return (props.activeStudents || 0) + (props.inactiveStudents || 0);
  });

  const activePct = computed(() => {
    const total = totalStatus.value || displayTotalStudents.value;
    return total > 0 ? Math.round((props.activeStudents / total) * 100) : 0;
  });

  const inactivePct = computed(() => {
    const total = totalStatus.value || displayTotalStudents.value;
    return total > 0 ? Math.round((props.inactiveStudents / total) * 100) : 0;
  });

  const graduatedPct = computed(() => {
    const total = displayTotalStudents.value || props.graduatedStudents;
    return total > 0 ? Math.round((props.graduatedStudents / total) * 100) : 0;
  });

  // Arc lengths for SVG Donut
  const activeLen = computed(() => {
    if (totalStatus.value <= 0) return 0;
    return (props.activeStudents / totalStatus.value) * circumference.value;
  });

  const inactiveLen = computed(() => {
    if (totalStatus.value <= 0) return 0;
    return (props.inactiveStudents / totalStatus.value) * circumference.value;
  });
</script>
